import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  api,
  ManagedTransformationEventSummary,
  TopViewingSitePlan,
} from '../../services/api';
import {
  formatQrRefreshIntervalInput,
  parseQrRefreshIntervalInput,
} from '../../utils/qrRefreshInterval';

const RECRUITMENT_CATEGORY = 'Recruitment';

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '-';
  const fmt = (d: Date) =>
    d.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate();
  if (sameDay) {
    const timeFmt = (d: Date) =>
      d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${fmt(s)} — ${timeFmt(e)}`;
  }
  return `${fmt(s)} — ${fmt(e)}`;
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return toDatetimeLocalValue(d);
}

function startOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function minDatetimeLocal(): string {
  return toDatetimeLocalValue(startOfTodayLocal());
}

interface ManageTransformationEventsTabProps {
  open: boolean;
  categories: string[];
  projects: TopViewingSitePlan[];
}

interface EventFormState {
  name: string;
  description: string;
  venue: string;
  start: string;
  end: string;
  categories: Set<string>;
  projectIds: Set<string>;
  trainers: Array<{ ceaNumber: string; name: string | null; mobile?: string | null }>;
  locationRequired: boolean;
  qrRefreshIntervalSeconds: number;
  qrRefreshIntervalInput: string;
}

type ToastState = { severity: 'success' | 'error'; message: string } | null;

/**
 * Admin tab for creating / viewing / editing / deleting TransformationEvent
 * rows. Categorization (categories, projects, trainers) is set in the
 * create / edit dialog.
 */
const ManageTransformationEventsTab: React.FC<ManageTransformationEventsTabProps> = ({
  open,
  categories,
  projects,
}) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<ManagedTransformationEventSummary[]>([]);
  const [query, setQuery] = useState('');
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<EventFormState>({
    name: '',
    description: '',
    venue: '',
    start: '',
    end: '',
    categories: new Set(),
    projectIds: new Set(),
    trainers: [],
    locationRequired: false,
    qrRefreshIntervalSeconds: 0,
    qrRefreshIntervalInput: '0',
  });
  const [trainerSearchInput, setTrainerSearchInput] = useState('');
  const [trainerSearchOptions, setTrainerSearchOptions] = useState<
    Array<{ ceaNumber: string; name: string | null }>
  >([]);
  const [trainerSearchLoading, setTrainerSearchLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listTransformationEvents({
        includePast: true,
        includeUpcoming: true,
      });
      setEvents(
        list.map((e) => ({
          ...e,
          assignedCategories: (e.assignedCategories || []).map((c) => c.toUpperCase()),
        })),
      );
    } catch (e) {
      setEvents([]);
      setToast({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to load ad-hoc events',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  useEffect(() => {
    const q = trainerSearchInput.trim();
    if (!formOpen || q.length <= 2) {
      setTrainerSearchOptions([]);
      setTrainerSearchLoading(false);
      return;
    }
    let cancelled = false;
    setTrainerSearchLoading(true);
    const timer = window.setTimeout(() => {
      void api
        .searchAgentsByCea(q)
        .then((agents) => {
          if (!cancelled) setTrainerSearchOptions(agents);
        })
        .catch(() => {
          if (!cancelled) setTrainerSearchOptions([]);
        })
        .finally(() => {
          if (!cancelled) setTrainerSearchLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [trainerSearchInput, formOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.venue || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q),
    );
  }, [events, query]);

  const focusedEvent = useMemo(
    () => events.find((e) => e.id === focusedId) || null,
    [events, focusedId],
  );

  const formCategoryOptions = useMemo(
    () =>
      categories.filter(
        (c) => c.toUpperCase() !== 'CPD' && c.toUpperCase() !== 'GENERIC CPD',
      ),
    [categories],
  );

  const projectOptions = useMemo(
    () =>
      projects
        .map((p) => ({
          projectId: p.projectId,
          projectName: p.projectName || p.projectId,
        }))
        .filter((p) => p.projectId),
    [projects],
  );

  const recruitmentSelected = form.categories.has(RECRUITMENT_CATEGORY.toUpperCase());

  const resetFormFields = (): EventFormState => ({
    name: '',
    description: '',
    venue: '',
    start: '',
    end: '',
    categories: new Set(),
    projectIds: new Set(),
    trainers: [],
    locationRequired: false,
    qrRefreshIntervalSeconds: 0,
    qrRefreshIntervalInput: '0',
  });

  const openCreate = () => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    const end = new Date(start);
    end.setHours(end.getHours() + 2);
    setFormMode('create');
    setEditingId(null);
    setForm({
      ...resetFormFields(),
      start: toDatetimeLocalValue(start),
      end: toDatetimeLocalValue(end),
    });
    setTrainerSearchInput('');
    setTrainerSearchOptions([]);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (event: ManagedTransformationEventSummary) => {
    setFormMode('edit');
    setEditingId(event.id);
    setForm({
      name: event.name,
      description: event.description || '',
      venue: event.venue || '',
      start: isoToDatetimeLocal(event.startTime),
      end: isoToDatetimeLocal(event.endTime),
      categories: new Set((event.assignedCategories || []).map((c) => c.toUpperCase())),
      projectIds: new Set((event.assignedProjects || []).map((p) => p.projectId)),
      trainers: [...(event.assignedTrainers || [])],
      locationRequired: event.locationRequired === true,
      qrRefreshIntervalSeconds: event.qrRefreshIntervalSeconds ?? 0,
      qrRefreshIntervalInput: formatQrRefreshIntervalInput(event.qrRefreshIntervalSeconds ?? 0),
    });
    setTrainerSearchInput('');
    setTrainerSearchOptions([]);
    setFormError(null);
    setFormOpen(true);
  };

  const toggleFormCategory = (cat: string) => {
    const upper = cat.toUpperCase();
    setForm((prev) => {
      const next = new Set(prev.categories);
      if (upper === RECRUITMENT_CATEGORY.toUpperCase()) {
        if (next.has(upper)) {
          return { ...prev, categories: new Set() };
        }
        return { ...prev, categories: new Set([upper]), projectIds: new Set() };
      }
      next.delete(RECRUITMENT_CATEGORY.toUpperCase());
      if (next.has(upper)) next.delete(upper);
      else next.add(upper);
      return { ...prev, categories: next };
    });
  };

  const handleSaveForm = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const start = new Date(form.start);
      const end = new Date(form.end);
      if (!form.name.trim()) throw new Error('Name is required');
      if (!form.description.trim()) throw new Error('Description is required');
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        throw new Error('Start and end times are required');
      }
      if (end <= start) throw new Error('End time must be after start time');
      if (formMode === 'create' && start < startOfTodayLocal()) {
        throw new Error('Start time must be today or later');
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        venue: form.venue.trim() || null,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        categories: Array.from(form.categories),
        projectIds: Array.from(form.projectIds),
        trainerCeaNumbers: form.trainers.map((t) => t.ceaNumber),
        locationRequired: form.locationRequired,
        qrRefreshIntervalSeconds: form.qrRefreshIntervalSeconds,
      };

      if (formMode === 'create') {
        const created = await api.createTransformationManagedEvent(payload);
        setFormOpen(false);
        await load();
        setFocusedId(created.id);
        setToast({ severity: 'success', message: `Created “${created.name}”.` });
      } else if (editingId != null) {
        const updated = await api.updateTransformationManagedEvent(editingId, payload);
        setFormOpen(false);
        await load();
        setFocusedId(updated.id);
        setToast({ severity: 'success', message: `Updated “${updated.name}”.` });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save event';
      setFormError(message);
      setToast({ severity: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event: ManagedTransformationEventSummary) => {
    const confirmed = window.confirm(
      `Delete ad-hoc event “${event.name}”? This cannot be undone from the admin UI.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await api.deleteTransformationManagedEvent(event.id);
      if (focusedId === event.id) setFocusedId(null);
      await load();
      setToast({ severity: 'success', message: `Deleted “${event.name}”.` });
    } catch (e) {
      setToast({
        severity: 'error',
        message: e instanceof Error ? e.message : 'Failed to delete event',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 1fr) 1fr' },
          gap: 2,
          minHeight: 480,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search ad-hoc events..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              Create
            </Button>
            <IconButton size="small" onClick={() => void load()} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: '1px solid rgba(var(--color-text-rgb), 0.1)',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 520,
            }}
          >
            {loading && events.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No ad-hoc events yet. Click Create to add one.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filtered.map((event) => {
                  const selected = focusedId === event.id;
                  return (
                    <ListItemButton
                      key={event.listId}
                      selected={selected}
                      onClick={() => setFocusedId(event.id)}
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {event.name}
                            </Typography>
                            <Chip
                              label="Ad-hoc"
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Stack>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" component="span" display="block">
                              {formatDateRange(event.startTime, event.endTime)}
                            </Typography>
                            {event.venue && (
                              <Typography
                                variant="caption"
                                component="span"
                                color="text.secondary"
                              >
                                {event.venue}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        <Box sx={{ minHeight: 0 }}>
          {!focusedEvent ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Select an event on the left to view its details.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {focusedEvent.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateRange(focusedEvent.startTime, focusedEvent.endTime)}
                  </Typography>
                  {focusedEvent.venue && (
                    <Typography variant="body2" color="text.secondary">
                      {focusedEvent.venue}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Edit event">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(focusedEvent)}
                      aria-label="Edit event"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete event">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => void handleDelete(focusedEvent)}
                        disabled={deleting}
                        aria-label="Delete event"
                      >
                        {deleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>

              {((focusedEvent.assignedCategories || []).length > 0 ||
                (focusedEvent.assignedProjects || []).length > 0) && (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                  {(focusedEvent.assignedCategories || []).map((c) => (
                    <Chip
                      key={c}
                      size="small"
                      label={c}
                      sx={{ height: 22, fontSize: '0.72rem' }}
                    />
                  ))}
                  {(focusedEvent.assignedProjects || []).map((project) => (
                    <Chip
                      key={project.projectId}
                      size="small"
                      variant="outlined"
                      label={project.projectName || project.projectId}
                      sx={{ height: 22, fontSize: '0.72rem' }}
                    />
                  ))}
                </Stack>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    color: 'var(--color-text-strong)',
                    p: 1.5,
                    border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                    borderRadius: 1,
                    bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                    maxHeight: 320,
                    overflow: 'auto',
                  }}
                >
                  {focusedEvent.description || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Trainers
                </Typography>
                {(focusedEvent.assignedTrainers || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No trainers assigned.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>CEANumber</TableCell>
                          <TableCell>Mobile</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(focusedEvent.assignedTrainers || []).map((trainer) => (
                          <TableRow key={trainer.ceaNumber}>
                            <TableCell>{trainer.name || '—'}</TableCell>
                            <TableCell>{trainer.ceaNumber}</TableCell>
                            <TableCell>{trainer.mobile || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: 1200, width: '100%' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {formMode === 'create' ? 'Create Ad-hoc Event' : 'Edit Ad-hoc Event'}
          <IconButton size="small" onClick={() => setFormOpen(false)} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            <TextField
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              required
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              size="small"
            />
            <TextField
              label="Venue (optional)"
              value={form.venue}
              onChange={(e) => setForm((prev) => ({ ...prev, venue: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Start time"
              type="datetime-local"
              required
              value={form.start}
              onChange={(e) => setForm((prev) => ({ ...prev, start: e.target.value }))}
              fullWidth
              size="small"
              inputProps={formMode === 'create' ? { min: minDatetimeLocal() } : undefined}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End time"
              type="datetime-local"
              required
              value={form.end}
              onChange={(e) => setForm((prev) => ({ ...prev, end: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{
                min: form.start || (formMode === 'create' ? minDatetimeLocal() : undefined),
              }}
              InputLabelProps={{ shrink: true }}
            />
            {formMode === 'create' && (
              <Typography variant="caption" color="text.secondary">
                Only dates from today onwards can be selected for new events.
              </Typography>
            )}

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.locationRequired}
                    onChange={(_, checked) =>
                      setForm((prev) => ({ ...prev, locationRequired: checked }))
                    }
                  />
                }
                label="Requires attendee location"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                When unchecked, agents do not need to enable device location to mark attendance.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                QR refresh interval
              </Typography>
              <TextField
                size="small"
                value={form.qrRefreshIntervalInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    qrRefreshIntervalInput: raw,
                    qrRefreshIntervalSeconds: parseQrRefreshIntervalInput(raw),
                  }));
                }}
                onBlur={() => {
                  setForm((prev) => ({
                    ...prev,
                    qrRefreshIntervalInput: formatQrRefreshIntervalInput(prev.qrRefreshIntervalSeconds),
                  }));
                }}
                placeholder="30"
                helperText="0 = do not refresh the QR."
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">seconds</InputAdornment>
                  ),
                }}
                sx={{ width: 140 }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Categories
              </Typography>
              {formCategoryOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No categories available.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {formCategoryOptions.map((c) => {
                    const upper = c.toUpperCase();
                    const checked = form.categories.has(upper);
                    const disabled =
                      recruitmentSelected && upper !== RECRUITMENT_CATEGORY.toUpperCase();
                    return (
                      <Chip
                        key={c}
                        label={c}
                        onClick={() => {
                          if (!disabled) toggleFormCategory(c);
                        }}
                        color={checked ? 'primary' : 'default'}
                        variant={checked ? 'filled' : 'outlined'}
                        clickable={!disabled}
                        disabled={disabled}
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Projects
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                disabled={recruitmentSelected}
                options={projectOptions}
                value={projectOptions.filter((o) => form.projectIds.has(o.projectId))}
                onChange={(_, value) => {
                  setForm((prev) => ({
                    ...prev,
                    projectIds: new Set(value.map((v) => v.projectId)),
                  }));
                }}
                getOptionLabel={(opt) => opt.projectName || opt.projectId}
                isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    placeholder={form.projectIds.size === 0 ? 'Select projects' : ''}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={opt.projectId}
                      label={opt.projectName || opt.projectId}
                      size="small"
                    />
                  ))
                }
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Trainers
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                disabled={recruitmentSelected}
                options={trainerSearchOptions}
                filterOptions={(x) => x}
                loading={trainerSearchLoading}
                value={form.trainers}
                inputValue={trainerSearchInput}
                onInputChange={(_, value, reason) => {
                  if (reason === 'reset') {
                    setTrainerSearchInput('');
                    return;
                  }
                  if (reason === 'input' || reason === 'clear') {
                    setTrainerSearchInput(value);
                  }
                }}
                onChange={(_, value) => {
                  setForm((prev) => ({ ...prev, trainers: value }));
                  setTrainerSearchInput('');
                }}
                getOptionLabel={(opt) =>
                  opt.name ? `${opt.name} (${opt.ceaNumber})` : opt.ceaNumber
                }
                isOptionEqualToValue={(option, value) =>
                  option.ceaNumber.toUpperCase() === value.ceaNumber.toUpperCase()
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    placeholder={
                      form.trainers.length === 0
                        ? 'Search CEANumber (min 3 characters)'
                        : ''
                    }
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={opt.ceaNumber}
                      label={
                        opt.name ? `${opt.name} (${opt.ceaNumber})` : opt.ceaNumber
                      }
                      size="small"
                    />
                  ))
                }
                noOptionsText={
                  trainerSearchInput.trim().length <= 2
                    ? 'Type at least 3 characters of a CEANumber'
                    : trainerSearchLoading
                      ? 'Searching…'
                      : 'No matching agents'
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSaveForm()} disabled={saving}>
            {saving
              ? formMode === 'create'
                ? 'Creating…'
                : 'Saving…'
              : formMode === 'create'
                ? 'Create'
                : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            sx={{ width: '100%' }}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ManageTransformationEventsTab;
